import React, { useCallback, useEffect, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import AuthService from '../components/services/AuthService';
import { getUserAsync } from '../state/auth/authSlice';
import { AppDispatch, RootState } from '../state/store';

import '../ui/css/LoginPage.css';
import '../ui/css/EmployeeCard.css';
import '../ui/css/SetPasswordPage.css';

const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

const passwordSchema = z
  .string()
  .min(1, 'Пароль обязателен')
  .min(8, 'Пароль должен содержать не менее 8 символов.')
  .max(20, 'Максимальная длина пароля составляет 20 символов.')
  .refine(
    (value) => passwordRegex.test(value),
    'Пароль должен содержать латинские буквы (строчные и заглавные), цифры и спецсимволы'
  );

const setPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Повторите пароль')
  })
  .superRefine((data, ctx) => {
    if (data.password && data.confirmPassword && data.password !== data.confirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Пароли не совпадают',
        path: ['confirmPassword']
      });
    }
  });

type SetPasswordFormData = z.infer<typeof setPasswordSchema>;

const SetPasswordPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const errorMessage = useSelector(
    (state: RootState) => state.auth.errorMessage
  );

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    watch
  } = useForm<SetPasswordFormData>({
    resolver: zodResolver(setPasswordSchema),
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const passwordValue = watch('password');

  useEffect(() => {
    if (passwordValue) {
      trigger('password');
    }
  }, [passwordValue, trigger]);

  const onSubmit = useCallback(
    async (data: SetPasswordFormData) => {
      setIsSubmitting(true);
      setSubmitError('');
      try {
        await AuthService.updatePassword(data.password);

        await dispatch(getUserAsync());

        sessionStorage.removeItem('pendingLoginData');
        navigate('/');
      } catch (error: any) {
        console.error('Ошибка при установке пароля:', error);
        setSubmitError(
          error?.response?.data?.error?.message ||
            'Произошла ошибка при установке пароля. Попробуйте еще раз.'
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch, navigate]
  );

  return (
    <div className="login-page wrapper wrapper--login-page">
      <img
        src="/Delo-Trak_Logo_Color_1.png"
        alt="Дело.Трек"
        style={{ height: 'auto', maxWidth: '300px', marginBottom: '20px' }}
      />
      <div className="description">
        <div className="p-inline-message p-component p-inline-message-warn" role="alert" aria-live="polite" data-pc-name="message" data-pc-section="root">
          <span className="p-inline-message-text" data-pc-section="text">
            В целях безопасности установите сложный постоянный пароль, который
        включает: не менее 8 символов, сочетание букв верхнего и нижнего
        регистра, цифр и специальных знаков.
          </span>
        </div>
      </div>
      <form className="autorization-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="autorization-form__message">Установка пароля</div>
        <Divider />
        <div className="flex flex-column md:flex-row gap-3 pb-0">
          <div className="flex flex-column w-full">
            <div className="flex flex-column fixed-row">
              <div className="flex justify-content-between align-items-center">
                <label htmlFor="password" className="col">Пароль:</label>
                <div
                  className={`p-inputgroup col-8 ${
                    errors.password ? 'error-color' : ''
                  }`}
                >
                  <InputText
                    {...register('password', {
                      onChange: () => trigger('password')
                    })}
                    autoComplete="new-password"
                    className="user-card__input"
                    placeholder="•••••"
                    type={isPasswordVisible ? 'text' : 'password'}
                    required
                  />
                  <span className="p-inputgroup-addon">
                    <i
                      className={
                        isPasswordVisible ? 'pi pi-eye-slash' : 'pi pi-eye'
                      }
                      onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                      style={{ cursor: 'pointer' }}
                    />
                  </span>
                </div>
              </div>
              <p
                className={`set-password-card-errors-password card-errors ${
                  errors.password ? 'text-red-500' : 'text-transparent'
                }`}
              >
                {errors.password ? errors.password.message : ' '}
              </p>
            </div>

            <div className="flex flex-column mb-2">
              <div className="flex justify-content-between align-items-center gap-3">
                <label htmlFor="confirmPassword" className="col">Повторите пароль:</label>
                <div
                  className={`p-inputgroup col-8 ${
                    errors.confirmPassword ? 'error-color' : ''
                  }`}
                >
                  <InputText
                    {...register('confirmPassword')}
                    autoComplete="new-password"
                    className="user-card__input"
                    placeholder="•••••"
                    type={isConfirmPasswordVisible ? 'text' : 'password'}
                    required
                  />
                  <span className="p-inputgroup-addon">
                    <i
                      className={
                        isConfirmPasswordVisible
                          ? 'pi pi-eye-slash'
                          : 'pi pi-eye'
                      }
                      onClick={() =>
                        setIsConfirmPasswordVisible(!isConfirmPasswordVisible)
                      }
                      style={{ cursor: 'pointer' }}
                    />
                  </span>
                </div>
              </div>
              <p
                className={`set-password-card-errors-password card-errors ${
                  errors.confirmPassword ? 'text-red-500' : 'text-transparent'
                }`}
              >
                {errors.confirmPassword
                  ? errors.confirmPassword.message
                  : ' '}
              </p>
            </div>
          </div>
        </div>
        <Button
          label="Подтвердить"
          className="w-full"
          type="submit"
          disabled={isSubmitting}
        />
        {submitError && submitError !== '' && (
          <div className="flex flex-column error-message">{submitError}</div>
        )}
        {errorMessage && errorMessage !== '' && (
          <div className="flex flex-column error-message">{errorMessage}</div>
        )}
      </form>
    </div>
  );
};

export default SetPasswordPage;

